import React, { useState, useEffect } from 'react';
import { Save, Lock, Unlock, Bell, Check, X, User, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
import { localizeMessage } from '../utils/toast.js';
import './FollowSettings.css';

const API_BASE_URL = `${GREENWICH_BACKEND_URL}/api`;

const FollowSettings = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({
    followRequestsEnabled: true,
    notificationPreferences: { email: true, push: true, inApp: true },
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    fetchUserSettings();
    fetchCurrentUser();
  }, []);

  const getToken = () => localStorage.getItem('token');

  const fetchCurrentUser = async () => {
    try {
      const token = getToken();
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCurrentUser(data.user);
        }
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const fetchUserSettings = async () => {
    try {
      setLoading(true);
      const token = getToken();

      if (!token) {
        setError(t('followSettings.pleaseLogin'));
        return;
      }

      // Fetch user data to get current settings
      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSettings({
          followRequestsEnabled: data.user.followRequestsEnabled !== false,
          notificationPreferences: {
            email: data.user.notificationPreferences?.email !== false,
            push: data.user.notificationPreferences?.push !== false,
            inApp: data.user.notificationPreferences?.inApp !== false,
          },
        });
      } else {
        setError(data.error || t('followSettings.failedLoad'));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setError(t('followSettings.failedLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFollowRequests = () => {
    setSettings(prev => ({
      ...prev,
      followRequestsEnabled: !prev.followRequestsEnabled
    }));
  };

  const handleToggleNotification = (key) => {
    setSettings(prev => ({
      ...prev,
      notificationPreferences: { ...prev.notificationPreferences, [key]: !prev.notificationPreferences[key] },
    }));
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError(null);

      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/follow/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          followRequestsEnabled: settings.followRequestsEnabled,
          notificationPreferences: settings.notificationPreferences,
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(t('followSettings.settingsSaved'));

        // Update current user data
        if (currentUser) {
          const updatedUser = {
            ...currentUser,
            followRequestsEnabled: settings.followRequestsEnabled
          };
          setCurrentUser(updatedUser);

          // Update localStorage
          localStorage.setItem('user', JSON.stringify(updatedUser));

          // Dispatch event for other components
          window.dispatchEvent(new CustomEvent('userProfileUpdated', {
            detail: { user: updatedUser }
          }));
        }

        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(data.error || t('followSettings.failedSave'));
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setError(t('followSettings.failedSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = () => {
    setSettings({
      followRequestsEnabled: true,
      notificationPreferences: { email: true, push: true, inApp: true },
    });
  };

  if (loading) {
    return (
      <div className="follow-settings-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('followSettings.loadingSettings')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="follow-settings-container">
      <div className="settings-header">
        <h2>{t('followSettings.title')}</h2>
        <p className="settings-subtitle">
          {t('followSettings.subtitle')}
        </p>
      </div>

      {error && (
        <div className="error-message">
          <X size={20} />
          <span>{localizeMessage(error)}</span>
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          <Check size={20} />
          <span>{successMessage}</span>
        </div>
      )}

      <div className="settings-content">
        <div className="settings-card">
          <div className="settings-card-header">
            <div className="setting-icon">
              {settings.followRequestsEnabled ? <Unlock size={24} /> : <Lock size={24} />}
            </div>
            <div className="setting-info">
              <h3>{t('followSettings.followRequests')}</h3>
              <p className="setting-description">
                {t('followSettings.followRequestsDesc')}
              </p>
            </div>
          </div>

          <div className="settings-toggle-section">
            <div className="toggle-info">
              <div className="toggle-label">
                <span className="toggle-title">{t('followSettings.requireApproval')}</span>
                <span className="toggle-subtitle">
                  {settings.followRequestsEnabled
                    ? t('followSettings.approvalOnDesc')
                    : t('followSettings.approvalOffDesc')}
                </span>
              </div>

              <div className="toggle-switch">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={settings.followRequestsEnabled}
                    onChange={handleToggleFollowRequests}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>

            <div className="setting-explanation">
              <div className="explanation-item">
                <div className="explanation-icon">
                  <Lock size={18} />
                </div>
                <div className="explanation-text">
                  <strong>{t('followSettings.whenOn')}</strong>
                  <ul>
                    <li>{t('followSettings.onPoint1')}</li>
                    <li>{t('followSettings.onPoint2')}</li>
                    <li>{t('followSettings.onPoint3')}</li>
                    <li>{t('followSettings.onPoint4')}</li>
                  </ul>
                </div>
              </div>

              <div className="explanation-item">
                <div className="explanation-icon">
                  <Unlock size={18} />
                </div>
                <div className="explanation-text">
                  <strong>{t('followSettings.whenOff')}</strong>
                  <ul>
                    <li>{t('followSettings.offPoint1')}</li>
                    <li>{t('followSettings.offPoint2')}</li>
                    <li>{t('followSettings.offPoint3')}</li>
                    <li>{t('followSettings.offPoint4')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="settings-card">
          <div className="settings-card-header">
            <div className="setting-icon">
              <Bell size={24} />
            </div>
            <div className="setting-info">
              <h3>{t('followSettings.notifications')}</h3>
              <p className="setting-description">
                {t('followSettings.notificationsDesc')}
              </p>
            </div>
          </div>

          <div className="notification-settings">
            <div className="notification-item">
              <div className="notification-info">
                <span className="notification-label">{t('followSettings.emailNotifications')}</span>
                <span className="notification-description">
                  {t('followSettings.emailNotifDesc')}
                </span>
              </div>
              <div className="notification-toggle">
                <label className="switch">
                  <input type="checkbox" checked={settings.notificationPreferences.email} onChange={() => handleToggleNotification('email')} />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>

            <div className="notification-item">
              <div className="notification-info">
                <span className="notification-label">{t('followSettings.pushNotifications')}</span>
                <span className="notification-description">
                  {t('followSettings.pushNotifDesc')}
                </span>
              </div>
              <div className="notification-toggle">
                <label className="switch">
                  <input type="checkbox" checked={settings.notificationPreferences.push} onChange={() => handleToggleNotification('push')} />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>

            <div className="notification-item">
              <div className="notification-info">
                <span className="notification-label">{t('followSettings.inAppNotifications')}</span>
                <span className="notification-description">
                  {t('followSettings.inAppNotifDesc')}
                </span>
              </div>
              <div className="notification-toggle">
                <label className="switch">
                  <input type="checkbox" checked={settings.notificationPreferences.inApp} onChange={() => handleToggleNotification('inApp')} />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="settings-stats">
          <div className="stat-card">
            <div className="stat-icon">
              <Unlock size={20} />
            </div>
            <div className="stat-info">
              <span className="stat-value">
                {currentUser?.followers?.length || 0}
              </span>
              <span className="stat-label">{t('followSettings.followers')}</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <User size={20} />
            </div>
            <div className="stat-info">
              <span className="stat-value">
                {currentUser?.following?.length || 0}
              </span>
              <span className="stat-label">{t('followSettings.following')}</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <Clock size={20} />
            </div>
            <div className="stat-info">
              <span className="stat-value">
                {currentUser?.pendingRequests || 0}
              </span>
              <span className="stat-label">{t('followSettings.pendingRequests')}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="settings-actions">
        <button
          className="reset-btn"
          onClick={handleResetToDefaults}
        >
          {t('followSettings.resetDefaults')}
        </button>

        <button
          className="save-btn"
          onClick={handleSaveSettings}
          disabled={saving}
        >
          {saving ? (
            <>
              <div className="spinner-small"></div>
              {t('followSettings.saving')}
            </>
          ) : (
            <>
              <Save size={18} />
              {t('followSettings.saveSettings')}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default FollowSettings;
